import { Navigate } from 'react-router-dom';
import { MapContainer, PlanningContainer, CalendarContainer } from '@aptive-env/aro-ui-module';
import { routes as comsRoutes } from '@aptive-env/coms-ui';
import { LicenseApplicationsView, RequirementsView, SalesRepresentativesView, ProfileView } from '@aptive-env/licensing-ui-module';
import { CustomOnboarding, ChangePassword, ForgotPassword, TokenError } from '@/modules/recruits/pages';
import { Login, Layout } from '@/components';
import { abilityConstants, dashboardConstants, pathConstants, userCan, userGroup } from '@/lib';
import {
  AllLeads,
  Dashboard,
  SalesOperations,
} from '@/modules/dashboard';
import {
  SalesPlanning,
  SalesPlanningAreas,
  SalesPlanningTeams,
  SalesPlanningAreaDetails,
  SalesPlanningTeamDetails,
  SalesPlanningAreaPolygons,
  SalesPlanningTeamPolygons,
} from '@/modules/SalesPlanning';
import {
  HousingAndVehicleForm,
  PersonalInfoForm,
  UniformForm,
  LicensingForm,
  HrForm,
} from '@/modules/recruits/forms';
import { DocumentsTab } from '@/modules/recruits/components/Contracts';
import { AttachmentsTab } from '@/modules/recruits/components/Attachments';
import { RepsApprovalPage } from '@/modules/RepsApproval';
import { adminToolsRoutes } from '@/modules/AdminTools/routing';
import { housingRoutes } from '@/modules/Housing/routing';
import HousingLayout from '@/modules/Housing/components/Layout';
import { UsersManagement, ArchivedLeadsPage } from '@/modules/dashboard/components';
import { archivedLeadsConstants } from '@/modules/dashboard/components/ArchivedLeads/lib';
import { usersManagementConstants } from '@/modules/dashboard/components/UsersManagement/lib';
import { AccessDenied, NotFound } from '@/pages';
import { SalesTeamsManagement } from '@/modules/dashboard/components/SalesTeamsManagement';
import { SalesAreasManagement } from '@/modules/dashboard/components/SalesAreasManagement';
import { AspynMigrator, Jobs, Settings } from '@/modules/AspynMigrator';

const {
  ARO_MODULE_FEATURE_FLAG_NAME,
  ARO_PLANNING_PAGE_FEATURE_FLAG_NAME,
  LICENSING_MODULE_FEATURE_FLAG_NAME,
  COMS_MODULE_FEATURE_FLAG_NAME,
  ASPYN_MIGRATOR_FEATURE_FLAG_NAME,
} = dashboardConstants;

const {
  ACCESS_ONB_ABILITY,
  ACCESS_ALL_LEADS_ABILITY,
  ACCESS_SALES_OPERATIONS_ABILITY,
  ACCESS_SPT_ABILITY,
  ACCESS_WIZARD_ABILITY,
  VIEW_AREAS_ABILITY,
  VIEW_TEAMS_ABILITY,
  VIEW_POLYGONS_ABILITY,
  ACCESS_USERS_MANAGEMENT_ABILITY,
  ACCESS_SALES_TEAMS_MANAGEMENT_ABILITY,
  ACCESS_SALES_AREAS_MANAGEMENT_ABILITY,
  ACCESS_REP_APPROVAL_ABILITY,
  ACCESS_ARO_ABILITY,
  ACCESS_LICENSING_ABILITY,
  ACCESS_LICENSING_PROFILE_ABILITY,
  ACCESS_COMS_ABILITY,
  ACCESS_ASPYN_MIGRATOR_ABILITY,
} = abilityConstants;

const {
  onboarding: {
    HOUSING_VEHICLE_PATH,
    UNIFORM_SWAG_PATH,
    LICENSING_PATH,
    HR_PATH,
    DOCUMENTS_PATH,
    ATTACHMENTS_PATH,
  },
} = pathConstants;

const mapRoutes = (routes, callback) => routes.map(({ children, ...route }) => ({
  ...callback(route),
  ...(children && { children: mapRoutes(children, callback) }),
}));

const routes = (isAuthenticated, user, redirectUrl, features = {}) => {
  const group = userGroup(user);
  const layout = <Layout user={user} isAuthenticated={isAuthenticated} />;
  const housingLayout = <HousingLayout user={user} isAuthenticated={isAuthenticated} />;
  const {
    [ARO_MODULE_FEATURE_FLAG_NAME]: isAroModuleEnabled,
    [ARO_PLANNING_PAGE_FEATURE_FLAG_NAME]: isAroPlanningPageEnabled,
    [LICENSING_MODULE_FEATURE_FLAG_NAME]: isLicensingModuleEnabled,
    [COMS_MODULE_FEATURE_FLAG_NAME]: isCOMSModuleEnabled,
    [ASPYN_MIGRATOR_FEATURE_FLAG_NAME]: isAspynMigratorEnabled,
  } = features;

  let defaultRoute;
  switch (true) {
    case userCan(user, ACCESS_ONB_ABILITY):
      defaultRoute = <Navigate to="/onboarding" />;

      break;
    case userCan(user, ACCESS_SPT_ABILITY):
      defaultRoute = <Navigate to="/sales-planning" />;

      break;
    case userCan(user, ACCESS_ARO_ABILITY):
      defaultRoute = <Navigate to="/aro" />;

      break;
    case userCan(user, ACCESS_LICENSING_ABILITY):
      defaultRoute = <Navigate to="/licensing" />;

      break;
    case userCan(user, ACCESS_ASPYN_MIGRATOR_ABILITY):
      defaultRoute = <Navigate to="/aspyn-migrator" />;

      break;
    default:
      defaultRoute = <Navigate to="/onboarding" />;
  }

  const protectedRoute = (element, ability) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }

    if (userCan(user, ability)) {
      return element;
    } else {
      return defaultRoute;
    }
  };

  const redirectRoute = () => {
    if (redirectUrl) {
      return <Navigate to={redirectUrl} />;
    } else {
      return defaultRoute;
    }
  };

  return [
    {
      path: '/',
      element: !isAuthenticated ? layout : redirectRoute(),
      children: [
        { path: '/', element: <Navigate to="/login" /> },
        { path: 'login', element: <Login /> },
      ],
    },
    {
      path: '/reset-password',
      element: !isAuthenticated ? layout : defaultRoute,
      children: [
        { path: '/reset-password', element: <ChangePassword /> },
      ],
    },
    {
      path: '/token-error',
      element: !isAuthenticated ? layout : defaultRoute,
      children: [
        { path: '/token-error', element: <TokenError /> },
      ],
    },
    {
      path: '/forgot-password',
      element: !isAuthenticated ? layout : defaultRoute,
      children: [
        { path: '/forgot-password', element: <ForgotPassword /> },
      ],
    },
    {
      path: '/aro',
      element: layout,
      children: [
        {
          index: true,
          element: <Navigate to="/aro/map" />,
        },
        {
          path: 'map',
          element: isAroModuleEnabled
            ? protectedRoute(<MapContainer />, ACCESS_ARO_ABILITY)
            : <AccessDenied />,
        },
        {
          path: 'planning',
          element: (isAroModuleEnabled && isAroPlanningPageEnabled)
            ? protectedRoute(<PlanningContainer />, ACCESS_ARO_ABILITY)
            : <AccessDenied />,
        },
        {
          path: 'calendar',
          element: isAroModuleEnabled
            ? protectedRoute(<CalendarContainer />, ACCESS_ARO_ABILITY)
            : <AccessDenied />,
        },
      ],
    },
    {
      path: '/licensing',
      element: layout,
      children: [
        {
          index: true,
          element: <Navigate to="/licensing/applications" />,
        },
        {
          path: 'applications',
          element: isLicensingModuleEnabled
            ? protectedRoute(<LicenseApplicationsView />, ACCESS_LICENSING_ABILITY)
            : <AccessDenied />,
        },
        {
          path: 'requirements',
          element: isLicensingModuleEnabled
            ? protectedRoute(<RequirementsView />, ACCESS_LICENSING_ABILITY)
            : <AccessDenied />,
        },
        {
          path: 'sales-reps',
          element: isLicensingModuleEnabled
            ? protectedRoute(<SalesRepresentativesView />, ACCESS_LICENSING_ABILITY)
            : <AccessDenied />,
        },
        {
          path: 'profile',
          element: isLicensingModuleEnabled
            ? protectedRoute(<ProfileView className="mt-[-41px]" />, ACCESS_LICENSING_PROFILE_ABILITY)
            : <AccessDenied />,
        },
      ],
    },
    {
      path: '/aspyn-migrator',
      element: layout,
      children: [
        {
          element: isAspynMigratorEnabled
            ? protectedRoute(<AspynMigrator />, ACCESS_ASPYN_MIGRATOR_ABILITY)
            : <AccessDenied />,
          children: [
            {
              index: true,
              element: isAspynMigratorEnabled
                ? protectedRoute(<Jobs />, ACCESS_ASPYN_MIGRATOR_ABILITY)
                : <AccessDenied />,
            },
            {
              path: 'settings',
              element: isAspynMigratorEnabled
                ? protectedRoute(<Settings />, ACCESS_ASPYN_MIGRATOR_ABILITY)
                : <AccessDenied />,
            },
          ],
        },
      ],
    },
    {
      path: '/onboarding',
      element: layout,
      children: [
        group !== 'N3' && {
          element: protectedRoute(<Dashboard />, ACCESS_ONB_ABILITY),
          children: [
            {
              index: true,
              element: protectedRoute(<SalesOperations />, ACCESS_SALES_OPERATIONS_ABILITY),
            },
            {
              path: '/onboarding/all-leads',
              element: protectedRoute(<AllLeads />, ACCESS_ALL_LEADS_ABILITY),
            },
            {
              path: '/onboarding/rep-approval',
              element: protectedRoute(<RepsApprovalPage />, ACCESS_REP_APPROVAL_ABILITY),
            },
            {
              path: '/onboarding/users-management',
              element: protectedRoute(
                <UsersManagement type={usersManagementConstants.ARCHIVED_USER_TYPE} />,
                ACCESS_USERS_MANAGEMENT_ABILITY,
              ),
            },
            {
              path: '/onboarding/archived-leads',
              element: protectedRoute(
                <ArchivedLeadsPage type={archivedLeadsConstants.ARCHIVED_LEAD_TYPE} />,
                ACCESS_USERS_MANAGEMENT_ABILITY,
              ),
            },
            {
              path: '/onboarding/sales-areas-management',
              element: protectedRoute(<SalesAreasManagement />, ACCESS_SALES_AREAS_MANAGEMENT_ABILITY),
            },
            {
              path: '/onboarding/sales-teams-management',
              element: protectedRoute(<SalesTeamsManagement />, ACCESS_SALES_TEAMS_MANAGEMENT_ABILITY),
            },
          ],
        },
        group === 'N3' && {
          path: '/onboarding',
          element: protectedRoute(<CustomOnboarding />, ACCESS_WIZARD_ABILITY),
          children: [
            {
              index: true,
              element: protectedRoute(<PersonalInfoForm />, ACCESS_WIZARD_ABILITY),
            },
            {
              path: `/onboarding/${HOUSING_VEHICLE_PATH}`,
              element: protectedRoute(<HousingAndVehicleForm />, ACCESS_WIZARD_ABILITY),
            },
            {
              path: `/onboarding/${UNIFORM_SWAG_PATH}`,
              element: protectedRoute(<UniformForm />, ACCESS_WIZARD_ABILITY),
            },
            {
              path: `/onboarding/${LICENSING_PATH}`,
              element: protectedRoute(<LicensingForm />, ACCESS_WIZARD_ABILITY),
            },
            {
              path: `/onboarding/${HR_PATH}`,
              element: protectedRoute(<HrForm />, ACCESS_WIZARD_ABILITY),
            },
            {
              path: `/onboarding/${DOCUMENTS_PATH}`,
              element: protectedRoute(<DocumentsTab />, ACCESS_WIZARD_ABILITY),
            },
            {
              path: `/onboarding/${ATTACHMENTS_PATH}`,
              element: protectedRoute(<AttachmentsTab />, ACCESS_WIZARD_ABILITY),
            },
          ],
        },
      ],
    },
    {
      path: '/sales-planning',
      element: layout,
      children: [
        {
          element: protectedRoute(<SalesPlanning />, ACCESS_SPT_ABILITY),
          children: [
            {
              index: true,
              element: protectedRoute(
                userCan(user, VIEW_AREAS_ABILITY)
                  ? <SalesPlanningAreas />
                  : <Navigate to="/sales-planning/teams" />,
                ACCESS_SPT_ABILITY,
              ),
            },
            {
              path: '/sales-planning/area/:areaId',
              element: protectedRoute(<SalesPlanningAreaDetails />, VIEW_AREAS_ABILITY),
            },
            {
              path: '/sales-planning/teams',
              element: protectedRoute(<SalesPlanningTeams />, VIEW_TEAMS_ABILITY),
            },
            {
              path: '/sales-planning/team/:teamId',
              element: protectedRoute(<SalesPlanningTeamDetails />, VIEW_TEAMS_ABILITY),
            },
            {
              path: '/sales-planning/polygons/area/:id',
              element: protectedRoute(<SalesPlanningAreaPolygons />, VIEW_POLYGONS_ABILITY),
            },
            {
              path: '/sales-planning/polygons/team/:id',
              element: protectedRoute(<SalesPlanningTeamPolygons />, VIEW_POLYGONS_ABILITY),
            },
          ],
        },
      ],
    },
    adminToolsRoutes(protectedRoute, layout),
    housingRoutes(protectedRoute, housingLayout),
    {
      element: layout,
      children: [
        ...(isCOMSModuleEnabled ? mapRoutes(comsRoutes, ({ element, ...route }) => ({
          ...route,
          element: userCan(user, ACCESS_COMS_ABILITY) ? element : <AccessDenied />,
        })) : []),
        // TODO: Move existing routes that use the same layout here
        // TODO: Add new routes that use the same layout here
        {
          path: '*',
          element: <NotFound />,
        },
      ],
    },
  ];
};

export default routes;
